import React from "react";

// Article 7 — Epilepsy in Pregnancy: Is the Medicine More Dangerous—or the Seizure?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

export function EpilepsyPregnancySeizureMedicine() {
  return (
    <article className="mt-10">
      <P>
        Most women with epilepsy can have a healthy pregnancy and baby. Some antiseizure medicines can affect fetal development, but uncontrolled convulsive seizures can also endanger both mother and baby. The safest plan preserves seizure control with the most appropriate medicine instead of abruptly stopping treatment.
      </P>
      <P>
        The useful question is: Which seizure type is being treated, which medicine controls it, how pregnancy changes that medicine, and what may happen if treatment is altered?
      </P>

      <H2>A Positive Pregnancy Test Is Not a Stop Signal</H2>
      <P>
        Do not stop, reduce or skip an antiseizure medicine after a positive pregnancy test. Sudden withdrawal can cause breakthrough seizures or a prolonged seizure emergency, particularly when treatment has been preventing generalised tonic-clonic or focal-to-bilateral tonic-clonic seizures. Contact the epilepsy specialist and obstetric team promptly, but continue treatment until a supervised plan is made. The 2024 AAN–AES–SMFM guideline advises caution when removing or replacing a medicine that is controlling convulsive seizures after pregnancy has begun. <Cite n="1" />
      </P>

      <H2>The Seizure Risk and the Medicine Risk Are Different</H2>
      <P>
        Not every seizure carries the same pregnancy risk. A brief focal seizure without loss of awareness is different from a convulsive seizure associated with a fall, trauma, aspiration or impaired oxygenation. Repeated or prolonged seizures require emergency treatment. Medicine-related risk also differs by drug, dose, combination and timing. The aim is not “no medicine.” It is the medicine and dose most likely to control epilepsy while minimising fetal exposure.
      </P>

      <H2>Antiseizure Medicines Are Not Interchangeable</H2>
      <P>
        Lamotrigine, levetiracetam and oxcarbazepine are among the medicines clinicians should consider, when appropriate for the epilepsy syndrome, to reduce major congenital-malformation risk. However, a medicine that appears favourable in pregnancy may not control every seizure type and may worsen some epilepsy syndromes. <Cite n="1" /> Preconception review should confirm the diagnosis, identify the lowest effective regimen, avoid unnecessary polytherapy and obtain a baseline drug level when monitoring is planned. Folic acid should begin before conception when possible. The AAN–AES–SMFM guideline recommends at least 0.4 mg daily; a higher dose may be prescribed according to local guidance and individual risk because the optimal dose remains uncertain. <Cite n="1" />
      </P>

      <H2>Valproate Needs Special Handling—not Sudden Withdrawal</H2>
      <P>
        Valproate has stronger evidence of dose-related congenital-malformation and adverse neurodevelopmental risks than many alternatives, so it should usually be avoided when another effective medicine can control the epilepsy. Topiramate also requires particular pregnancy counselling. <Cite n="1" /> Yet valproate may best control certain generalised epilepsies. A woman who discovers she is pregnant while taking it should not stop it herself. Urgent specialist review should address seizure history, dose, possible alternatives and fetal assessment. Accidental exposure is not proof that the baby has been harmed and is not, by itself, an automatic reason to end the pregnancy.
      </P>

      <H2>Pregnancy Can Lower Medicine Levels</H2>
      <P>
        Pregnancy changes kidney and liver handling of several antiseizure medicines. In the MONEAD study, dose-normalised concentrations fell substantially for lamotrigine and levetiracetam and declined for several other medicines. A previously effective dose may become insufficient even when every tablet is taken correctly. <Cite n="2" /> Monitoring should use seizure history, adherence, symptoms and—when appropriate—blood levels compared with the woman’s pre-pregnancy baseline. If doses rise, a written postpartum reduction plan is essential because clearance can return quickly after birth and cause toxicity if the pregnancy dose is continued. <Cite n="3,4" />
      </P>

      <H2>Birth, Breastfeeding and the Postpartum Period Need a Plan</H2>
      <P>
        Most women with epilepsy can have a vaginal birth; epilepsy alone does not require induction or caesarean delivery. Medicine should continue on schedule during labour, with a rescue-treatment plan if a seizure occurs. Avoiding missed doses, dehydration and severe sleep deprivation helps reduce risk. <Cite n="5" /> Breastfeeding is generally supported, with drug-specific advice and observation of the baby when needed. Early postpartum care matters because sleep loss, missed medicines and rapidly changing drug levels can combine to increase seizure or toxicity risk. <Cite n="4,5" />
      </P>

      <H2>A New or Changed Seizure Needs Urgent Assessment</H2>
      <P>
        Known epilepsy does not mean that every seizure in pregnancy is “just epilepsy.” A first seizure, a different pattern, repeated seizures or failure to recover normally requires urgent hospital assessment. After 20 weeks, a seizure with high blood pressure, severe headache, visual symptoms, upper abdominal pain, confusion or sudden illness may represent eclampsia or another emergency. It must not be managed by simply taking an extra routine epilepsy tablet at home. <Cite n="6" />
      </P>

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed">
          <strong>Dr Tania’s takeaway:</strong> Pregnancy does not make antiseizure medicine optional, and it does not make every medicine equally suitable. Protecting the baby begins by preventing dangerous seizures with the right drug, monitoring changing levels, planning postpartum dose reduction and treating any new or unusual seizure as an emergency.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Pack AM, Oskoui M, Williams Roberson S, et al. Teratogenesis, Perinatal, and Neurodevelopmental Outcomes After In Utero Exposure to Antiseizure Medication: Practice Guideline From the AAN, AES, and SMFM. <em>Neurology</em>. 2024;102(11):e209279. doi:10.1212/WNL.0000000000209279.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11175651/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC11175651/</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] Pennell PB, Karanam A, Meador KJ, et al. Antiseizure Medication Concentrations During Pregnancy: Results From the Maternal Outcomes and Neurodevelopmental Effects of Antiepileptic Drugs (MONEAD) Study. <em>JAMA Neurology</em>. 2022;79(4):370–379. doi:10.1001/jamaneurol.2021.5487.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://jamanetwork.com/journals/jamaneurology/fullarticle/2789145"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://jamanetwork.com/journals/jamaneurology/fullarticle/2789145</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] Pennell PB, Li D, Kerr WT, et al. Antiseizure Medication Dosing Strategy During Pregnancy and Early Postpartum in Women With Epilepsy in MONEAD. <em>Neurology</em>. 2026;106(2):e214483. doi:10.1212/WNL.0000000000214483.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC13027315/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC13027315/</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] National Institute for Health and Care Excellence. Epilepsies in children, young people and adults. NICE guideline NG217. Published April 27, 2022; updated January 30, 2025.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.nice.org.uk/guidance/ng217/chapter/principles-of-treatment-safety-monitoring-and-withdrawal"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.nice.org.uk/guidance/ng217/chapter/principles-of-treatment-safety-monitoring-and-withdrawal</a>
          </p>
        </li>
        <li id="ref-5" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[5] Tomson T, Battino D, Bromley R, et al. Management of epilepsy in pregnancy: a report from the International League Against Epilepsy Task Force on Women and Pregnancy. <em>Epileptic Disorders</em>. 2019;21(6):497–517. doi:10.1684/epd.2019.1105.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://onlinelibrary.wiley.com/doi/full/10.1684/epd.2019.1105"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://onlinelibrary.wiley.com/doi/full/10.1684/epd.2019.1105</a>
          </p>
        </li>
        <li id="ref-6" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[6] National Institute for Health and Care Excellence. Hypertension in pregnancy: diagnosis and management. NICE guideline NG133. Published June 25, 2019. Current online recommendations.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.nice.org.uk/guidance/ng133/chapter/Recommendations"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.nice.org.uk/guidance/ng133/chapter/Recommendations</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific neurology, maternal–fetal-medicine, obstetric or paediatric advice. Do not start, stop, reduce, substitute or take extra antiseizure medicine because of this page. Seek emergency care for a prolonged or repeated seizure, injury, breathing difficulty, failure to recover normally, severe headache, visual symptoms, high blood pressure or a first or changed seizure in pregnancy.
      </P>
    </article>
  );
}
